import {
  evaluateSearchQuery,
  parseSearchQuery,
  SearchQueryError,
  textOperands,
} from '@affine/env/search-query';
import type { IndexerPreferOptions, IndexerSyncState } from '@affine/nbstore';
import {
  type ReferenceParams,
  ReferenceParamsSchema,
} from '@blocksuite/affine/model';
import { fromPromise, LiveData, Service } from '@toeverything/infra';
import { isEmpty, omit } from 'lodash-es';
import {
  catchError,
  combineLatest,
  defer,
  distinctUntilChanged,
  finalize,
  map,
  type Observable,
  of,
  repeat,
  startWith,
  switchMap,
  timer,
} from 'rxjs';
import { z } from 'zod';

import { normalizeSearchText } from '../../../utils/normalize-search-text';
import type { DocsService } from '../../doc/services/docs';
import { toDocSearchParams } from '../../navigation/utils';
import type { WorkspaceService } from '../../workspace';

const IndexedReferenceSchema = ReferenceParamsSchema.extend({
  docId: z.string().min(1),
});

function parseIndexedReferences(value: unknown) {
  const payloads =
    typeof value === 'string'
      ? [value]
      : Array.isArray(value)
        ? value.filter((item): item is string => typeof item === 'string')
        : [];
  const refs: ({ docId: string } & ReferenceParams)[] = [];
  let malformed = Array.isArray(value)
    ? value.length - payloads.length
    : typeof value === 'string'
      ? 0
      : 1;

  for (const payload of payloads) {
    try {
      const result = IndexedReferenceSchema.safeParse(JSON.parse(payload));
      if (result.success) {
        refs.push(result.data);
      } else {
        malformed++;
      }
    } catch {
      malformed++;
    }
  }
  return { refs, malformed };
}

export type IndexedDocReference = {
  title: string;
  docId: string;
  params?: ReturnType<typeof toDocSearchParams>;
};

type DocSearchResult = {
  docId: string;
  title: string;
  score: number;
  blockId?: string;
  blockContent?: string;
};

type DocSearchState = {
  docs: DocSearchResult[];
  loading: boolean;
  error: string | null;
};

const stringField = (value: unknown) =>
  typeof value === 'string'
    ? value
    : Array.isArray(value) && typeof value[0] === 'string'
      ? value[0]
      : null;

const equalReferenceSets = (
  previous: readonly IndexedDocReference[],
  current: readonly IndexedDocReference[]
) => {
  if (previous.length !== current.length) return false;
  const currentIds = new Set(current.map(reference => reference.docId));
  return previous.every(reference => currentIds.has(reference.docId));
};

export class DocsSearchService extends Service {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly docsService: DocsService
  ) {
    super();
  }

  get indexer() {
    return this.workspaceService.workspace.engine.indexer;
  }

  readonly indexerState$ = LiveData.from(this.indexer.state$, {
    indexing: 0,
    errorMessage: null,
  } as IndexerSyncState);

  searchTitle$(query: string) {
    return this.indexer
      .search$(
        'doc',
        {
          type: 'match',
          field: 'title',
          match: query,
        },
        {
          pagination: {
            skip: 0,
            limit: Infinity,
          },
        }
      )
      .pipe(
        map(({ nodes }) => {
          return nodes.map(node => node.id);
        })
      );
  }

  private searchPage$(
    query: string,
    prefer: IndexerPreferOptions = 'remote',
    pagination = { limit: 50, skip: 0 }
  ) {
    const results$ = this.indexer
      .aggregate$(
        'block',
        {
          type: 'boolean',
          occur: 'must',
          queries: [
            {
              type: 'match',
              field: 'content',
              match: query,
            },
            {
              type: 'boolean',
              occur: 'should',
              queries: [
                {
                  type: 'match',
                  field: 'content',
                  match: query,
                },
                {
                  type: 'boost',
                  boost: 1.5,
                  query: {
                    type: 'match',
                    field: 'flavour',
                    match: 'affine:page',
                  },
                },
              ],
            },
          ],
        },
        'docId',
        {
          pagination: {
            ...pagination,
          },
          hits: {
            pagination: {
              limit: 2,
              skip: 0,
            },
            fields: ['blockId', 'flavour'],
            highlights: [
              {
                field: 'content',
                before: '<b>',
                end: '</b>',
              },
            ],
          },
          prefer,
        }
      )
      .pipe(
        map(({ buckets }) => {
          const result = [];

          for (const bucket of buckets) {
            const firstMatch = bucket.hits.nodes[0];
            if (!firstMatch) continue;
            const firstMatchFlavour = stringField(firstMatch.fields.flavour);
            if (firstMatchFlavour === 'affine:page') {
              // is title match
              const blockContent = normalizeSearchText(
                bucket.hits.nodes[1]?.highlights?.content?.[0]
              ); // try to get block content
              result.push({
                docId: bucket.key,
                title: normalizeSearchText(firstMatch.highlights?.content?.[0]),
                score: bucket.score,
                blockContent,
              });
            } else {
              const title =
                this.docsService.list.doc$(bucket.key).value?.title$.value ??
                '';
              const matchedBlockId = stringField(firstMatch.fields.blockId);
              // is block match
              result.push({
                docId: bucket.key,
                title: title,
                blockId: matchedBlockId ?? undefined,
                score: bucket.score,
                blockContent: normalizeSearchText(
                  firstMatch.highlights?.content?.[0]
                ),
              });
            }
          }

          return { docs: result, bucketCount: buckets.length };
        })
      );

    return results$;
  }

  search$(
    query: string,
    prefer: IndexerPreferOptions = 'remote'
  ): Observable<DocSearchResult[]> {
    const results$ = this.searchPage$(query, prefer).pipe(
      map(page => page.docs)
    );
    return prefer === 'local' ? this.withLocalPriority$(results$) : results$;
  }

  private withLocalPriority$<T>(results$: Observable<T>): Observable<T> {
    return this.docsService.list.nonTrashDocsIds$.pipe(
      switchMap(docIds =>
        defer(() => {
          const release = docIds.map(id => this.indexer.addPriority(id, 10));
          return results$.pipe(finalize(() => release.forEach(undo => undo())));
        })
      )
    );
  }

  /** Evaluate operands as document sets, never as a block-level boolean query. */
  searchExpression$(
    query: string,
    prefer: IndexerPreferOptions = 'remote'
  ): Observable<DocSearchResult[]> {
    return defer(() => {
      const expression = parseSearchQuery(query);
      const operands = [
        ...new Map(textOperands(expression).map(op => [op.key, op])).values(),
      ];
      const pageOptions = (skip: number, cursor?: string) => ({
        limit: 500,
        ...(cursor ? { cursor } : { skip }),
      });
      const nextPage = (
        pagination: {
          hasMore: boolean;
          nextCursor?: string | null;
          skip: number;
          limit: number;
        },
        skip: number,
        cursor?: string
      ) => {
        if (!pagination.hasMore) return null;
        if (pagination.nextCursor) {
          if (pagination.nextCursor === cursor)
            throw new Error('Search cursor did not advance');
          return { skip: 0, cursor: pagination.nextCursor };
        }
        const next = pagination.skip + pagination.limit;
        if (next <= skip || (prefer === 'remote' && next >= 10000)) {
          throw new Error(
            'Search server cannot traverse all matches; retrying locally'
          );
        }
        return { skip: next, cursor: undefined };
      };
      const operand$ = (
        match: string,
        skip = 0,
        cursor?: string
      ): Observable<DocSearchResult[]> =>
        this.indexer
          .search$(
            'block',
            { type: 'match', field: 'content', match },
            {
              prefer,
              pagination: pageOptions(skip, cursor),
              fields: ['docId', 'blockId', 'flavour'],
              highlights: [{ field: 'content', before: '<b>', end: '</b>' }],
            }
          )
          .pipe(
            switchMap(({ nodes, pagination }) => {
              const docs: DocSearchResult[] = nodes.flatMap(node => {
                const docId = stringField(node.fields.docId);
                if (!docId) return [];
                return [
                  {
                    docId,
                    score: Number.isFinite(node.score) ? node.score : 0,
                    title:
                      this.docsService.list.doc$(docId).value?.title$.value ??
                      '',
                    blockId:
                      stringField(node.fields.flavour) === 'affine:page'
                        ? undefined
                        : (stringField(node.fields.blockId) ?? undefined),
                    blockContent: normalizeSearchText(
                      node.highlights?.content?.[0]
                    ),
                  },
                ];
              });
              const next = nextPage(pagination, skip, cursor);
              return next
                ? operand$(match, next.skip, next.cursor).pipe(
                    map(rest => [...docs, ...rest])
                  )
                : of(docs);
            })
          );
      const title$ = (
        match: string,
        skip = 0,
        cursor?: string
      ): Observable<string[]> =>
        this.indexer
          .search$(
            'doc',
            { type: 'match', field: 'title', match },
            {
              prefer,
              fields: ['docId'],
              pagination: pageOptions(skip, cursor),
            }
          )
          .pipe(
            switchMap(({ nodes, pagination }) => {
              const ids = nodes.map(
                node => stringField(node.fields.docId) ?? node.id
              );
              const next = nextPage(pagination, skip, cursor);
              return next
                ? title$(match, next.skip, next.cursor).pipe(
                    map(rest => [...ids, ...rest])
                  )
                : of(ids);
            })
          );
      const matches$ = operands.length
        ? combineLatest(
            operands.map(op =>
              combineLatest([operand$(op.match), title$(op.match)]).pipe(
                map(([docs, titles]) => ({
                  op,
                  docs,
                  titles,
                  ids: new Set([...docs.map(doc => doc.docId), ...titles]),
                }))
              )
            )
          )
        : of([]);
      const midnight$ = defer(() => {
        const next = new Date();
        next.setHours(24, 0, 0, 0);
        return timer(next.getTime() - Date.now());
      }).pipe(repeat(), startWith(0));
      const results$ = combineLatest([
        matches$,
        this.docsService.allNonTrashDocIds$(),
        this.docsService.allDocsCreatedDate$(),
        this.docsService.allDocsUpdatedDate$(),
        this.docsService.allDocTitle$(),
        midnight$,
      ]).pipe(
        map(([matches, universe, created, updated, titles]) => {
          const dates = new Map<
            string,
            { created?: number; updated?: number }
          >();
          created.forEach(doc =>
            dates.set(doc.id, { created: doc.createDate || undefined })
          );
          updated.forEach(doc =>
            dates.set(doc.id, {
              ...dates.get(doc.id),
              updated: doc.updatedDate,
            })
          );
          const ids = evaluateSearchQuery(
            expression,
            universe,
            new Map(matches.map(m => [m.op.key, m.ids])),
            dates,
            {
              now: Date.now(),
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }
          );
          const snippets = new Map<string, DocSearchResult>();
          for (const match of matches.filter(m => m.op.positive)) {
            for (const doc of match.docs) {
              const previous = snippets.get(doc.docId);
              if (
                !previous ||
                doc.score > previous.score ||
                (doc.score === previous.score &&
                  !previous.blockContent &&
                  doc.blockContent)
              ) {
                snippets.set(doc.docId, doc);
              }
            }
          }
          const titleMatches = new Set(
            matches.filter(m => m.op.positive).flatMap(m => m.titles)
          );
          const names = new Map(titles.map(doc => [doc.id, doc.title]));
          return [...ids]
            .map(
              docId =>
                snippets.get(docId) ?? {
                  docId,
                  title: names.get(docId) ?? '',
                  score: 0,
                }
            )
            .map(doc => ({
              ...doc,
              score: doc.score + (titleMatches.has(doc.docId) ? 1.5 : 0),
            }))
            .sort((a, b) => b.score - a.score);
        })
      );
      return prefer === 'local' ? this.withLocalPriority$(results$) : results$;
    });
  }

  // The notes feed needs the same local fallback as quick search, including
  // indexing progress so a new index is not mistaken for an empty workspace.
  searchWithFallback$(query: string): Observable<DocSearchState> {
    const initial: DocSearchState = { docs: [], loading: true, error: null };
    const local$ = defer(() =>
      combineLatest([
        this.searchExpression$(query, 'local'),
        this.indexer.state$,
      ]).pipe(
        map(([docs, state]): DocSearchState => ({
          docs,
          loading: !state.completed && !state.errorMessage,
          error: state.errorMessage,
        }))
      )
    );
    const results$ =
      this.workspaceService.workspace.flavour === 'local'
        ? local$
        : this.searchExpression$(query, 'remote').pipe(
            catchError(() => of([])),
            switchMap(docs =>
              docs.length ? of({ docs, loading: false, error: null }) : local$
            )
          );
    return results$.pipe(
      startWith(initial),
      catchError(error =>
        of({
          docs: [],
          loading: false,
          error:
            error instanceof SearchQueryError
              ? `${error.message} (position ${error.position + 1})`
              : error instanceof Error
                ? error.message
                : String(error),
        })
      )
    );
  }

  watchRefsFrom(ids: string | string[]) {
    const docIds = Array.isArray(ids) ? ids : [ids];
    if (docIds.length === 0) {
      return of([]);
    }

    return this.watchRefsBySourceFrom(docIds).pipe(
      map((refsBySource): IndexedDocReference[] => {
        const refs = Array.from(refsBySource.values()).flat();
        return Array.from(
          new Map(
            refs
              .filter(ref => !docIds.includes(ref.docId))
              .map(ref => [ref.docId, ref])
          ).values()
        );
      }),
      distinctUntilChanged((previous, current) =>
        equalReferenceSets(previous, current)
      )
    );
  }

  watchRefsBySourceFrom(ids: string | string[]) {
    const docIds = Array.isArray(ids) ? ids : [ids];
    if (docIds.length === 0) {
      return of(new Map<string, IndexedDocReference[]>());
    }

    return this.indexer
      .search$(
        'block',
        {
          type: 'boolean',
          occur: 'must',
          queries: [
            {
              type: 'boolean',
              occur: 'should',
              queries: docIds.map(id => ({
                type: 'match',
                field: 'docId',
                match: id,
              })),
            },
            {
              type: 'exists',
              field: 'refDocId',
            },
          ],
        },
        {
          fields: ['docId', 'refDocId', 'ref'],
          pagination: {
            limit: Infinity,
          },
        }
      )
      .pipe(
        switchMap(({ nodes }) => {
          return fromPromise(async () => {
            let malformed = 0;
            const refsBySource = new Map<
              string,
              Map<string, { docId: string } & ReferenceParams>
            >();
            for (const node of nodes) {
              const sourceId = stringField(node.fields.docId);
              const parsed = parseIndexedReferences(node.fields.ref);
              malformed += parsed.malformed;
              if (!sourceId || !docIds.includes(sourceId)) continue;
              let sourceRefs = refsBySource.get(sourceId);
              if (!sourceRefs) {
                sourceRefs = new Map();
                refsBySource.set(sourceId, sourceRefs);
              }
              for (const ref of parsed.refs) {
                if (ref.docId !== sourceId) sourceRefs.set(ref.docId, ref);
              }
            }
            if (malformed > 0) {
              console.warn('[docs-search] skipped malformed references', {
                count: malformed,
              });
            }

            return new Map(
              docIds.map(sourceId => [
                sourceId,
                Array.from(refsBySource.get(sourceId)?.values() ?? []).flatMap(
                  ref => {
                    const doc = this.docsService.list.doc$(ref.docId).value;
                    if (!doc) return [];
                    const params = omit(ref, ['docId']);
                    return [
                      {
                        title: doc.title$.value,
                        docId: doc.id,
                        params: isEmpty(params)
                          ? undefined
                          : toDocSearchParams(params),
                      },
                    ];
                  }
                ),
              ])
            );
          });
        }),
        distinctUntilChanged((previous, current) =>
          docIds.every(sourceId =>
            equalReferenceSets(
              previous.get(sourceId) ?? [],
              current.get(sourceId) ?? []
            )
          )
        )
      );
  }

  watchDatabasesTo(docId: string) {
    const DatabaseAdditionalSchema = z.object({
      databaseName: z.string().optional(),
    });
    return this.indexer
      .search$(
        'block',
        {
          type: 'boolean',
          occur: 'must',
          queries: [
            {
              type: 'match',
              field: 'refDocId',
              match: docId,
            },
            {
              type: 'match',
              field: 'parentFlavour',
              match: 'affine:database',
            },
          ],
        },
        {
          fields: ['docId', 'blockId', 'parentBlockId', 'additional'],
          pagination: {
            limit: 100,
          },
        }
      )
      .pipe(
        map(({ nodes }) => {
          return nodes
            .map(node => {
              if (node.fields.docId === docId) {
                // Ignore if it is a link to the current document.
                return null;
              }

              const additional =
                typeof node.fields.additional === 'string'
                  ? node.fields.additional
                  : node.fields.additional[0];

              return {
                docId:
                  typeof node.fields.docId === 'string'
                    ? node.fields.docId
                    : node.fields.docId[0],
                rowId:
                  typeof node.fields.blockId === 'string'
                    ? node.fields.blockId
                    : node.fields.blockId[0],
                databaseBlockId:
                  typeof node.fields.parentBlockId === 'string'
                    ? node.fields.parentBlockId
                    : node.fields.parentBlockId[0],
                databaseName: DatabaseAdditionalSchema.safeParse(additional)
                  .data?.databaseName as string | undefined,
              };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
        })
      );
  }

  watchDocSummary(docId: string) {
    return this.indexer
      .search$(
        'doc',
        {
          type: 'match',
          field: 'docId',
          match: docId,
        },
        {
          fields: ['summary'],
          pagination: {
            limit: 1,
          },
        }
      )
      .pipe(
        map(({ nodes }) => {
          const node = nodes.at(0);
          return (
            (typeof node?.fields.summary === 'string'
              ? node?.fields.summary
              : node?.fields.summary[0]) ?? null
          );
        })
      );
  }
}
